import { ImportIcon } from "@cynthai/icons";
import { Button } from "@cynthai/ui";
import { type ChangeEvent, useRef } from "react";

import { csvErrorToast, readCsvText } from "@/core/lib/csv";
import { addToast } from "@/core/lib/toast";
import type { DraftRow } from "@/features/classification/lib/statement-tree";
import { parseStatementTreeCsv } from "@/features/classification/lib/statement-tree-csv";
import { m } from "@/paraglide/messages";

export function StatementTreeCsvImportButton({
  isDisabled,
  onImport,
}: Readonly<{
  isDisabled?: boolean;
  onImport: (rows: DraftRow[]) => void;
}>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const showError = (title: string, error: unknown = new Error(title)) => {
    addToast({ title, severity: "danger", error });
  };

  const importFile = async (file: File) => {
    try {
      const { text } = await readCsvText(file);
      const result = parseStatementTreeCsv(text);
      if (result.status === "ok") {
        onImport(result.rows);
        addToast({
          title: m.statement_tree_csv_import_success({
            count: result.rows.length,
          }),
          severity: "success",
        });
        return;
      }
      if (result.status === "missing_columns") {
        showError(
          m.statement_tree_csv_import_missing_columns({
            columns: result.columns.join(", "),
          }),
        );
        return;
      }
      if (result.status === "invalid_rows") {
        showError(
          m.statement_tree_csv_import_invalid_rows({
            lines: result.lines.join(", "),
          }),
        );
        return;
      }
      showError(
        result.status === "empty"
          ? m.classification_import_error_empty()
          : m.toast_failed_to_parse_csv_file(),
      );
    } catch (error) {
      addToast(csvErrorToast(error));
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void importFile(file);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        disabled={isDisabled}
        aria-label={m.classification_import_action()}
        onChange={onFileChange}
      />
      <Button
        variant="bordered"
        color="secondary"
        icon={ImportIcon}
        isDisabled={isDisabled}
        onPress={() => inputRef.current?.click()}
      >
        {m.classification_import_action()}
      </Button>
    </>
  );
}
